import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Alert, ArrowTabStepper, Button, Card, useToast,
  isEmail, isPhone, isPan, isGstin,
} from '@kss/ui';
import { apiClient } from '@kss/api';
import {
  ArrowLeft, ArrowRight, Building2, Check, CreditCard, KeyRound,
  Lock, Mail, Phone, ShieldCheck, User, UserPlus,
} from 'lucide-react';

/**
 * Self-serve tenant signup.
 *
 * 4-step wizard:
 *   1. Organisation        — business name, GSTIN (opt), PAN, country
 *   2. Administrator       — name, email, mobile, password
 *   3. Subscription plan   — pick a plan from /tenancy/SubscriptionPlan
 *      (with a Starter fallback when the API is empty)
 *   4. Checkout & done     — POST /tenancy/TenantOnboardingRequest,
 *                           hand off to the payment processor when the
 *                           plan has a price; auto-skip for free plans.
 *
 * On success we land on the panel sign-in modal pre-filled with the
 * tenant code we just registered.
 */

type Step = 0 | 1 | 2 | 3;

interface PlanRow extends Record<string, unknown> {
  subscriptionPlanId?: string;
  id?: number | string;
  code?: string;
  name?: string;
  description?: string;
  priceMonthly?: number;
  priceYearly?: number;
  currency?: string;
  features?: string[] | string;
  isActive?: boolean | number;
}

interface OrgInfo {
  tenantCode: string;
  businessName: string;
  gstin: string;
  pan: string;
  country: string;
  state: string;
  city: string;
  postalCode: string;
}

interface AdminInfo {
  name: string;
  email: string;
  phone: string;
  password: string;
  confirm: string;
}

const FALLBACK_PLANS: PlanRow[] = [
  {
    subscriptionPlanId: 'starter',
    code: 'STARTER',
    name: 'Starter',
    description: '14-day trial · single-branch · core features',
    priceMonthly: 0,
    currency: 'INR',
    features: ['100 invoices/mo', '1 user', 'Basic POS + inventory', 'Email support'],
    isActive: 1,
  },
  {
    subscriptionPlanId: 'growth',
    code: 'GROWTH',
    name: 'Growth',
    description: 'For multi-branch retailers and restaurants',
    priceMonthly: 999,
    currency: 'INR',
    features: ['Unlimited invoices', 'e-Invoice + e-Way Bill', '10 users · multi-branch', 'Priority support'],
    isActive: 1,
  },
  {
    subscriptionPlanId: 'business',
    code: 'BUSINESS',
    name: 'Business',
    description: 'Full ERP — accounting, payroll, CRM, AI',
    priceMonthly: 2499,
    currency: 'INR',
    features: ['Everything in Growth', 'Unlimited users', 'Accounting + Payroll + HRM', 'Dedicated CSM'],
    isActive: 1,
  },
];

const STEPS = [
  { key: 'org',      label: 'Organisation' },
  { key: 'admin',    label: 'Administrator' },
  { key: 'plan',     label: 'Plan' },
  { key: 'checkout', label: 'Checkout' },
];

const inputCls =
  'h-10 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] px-3 text-sm text-[var(--color-text-primary)] outline-none focus:border-[var(--color-primary)]';

export default function TenantSignup() {
  const navigate = useNavigate();
  const toast = useToast();

  const [step, setStep] = useState<Step>(0);
  const [savedIndices, setSavedIndices] = useState<number[]>([]);

  const [org, setOrg] = useState<OrgInfo>({
    tenantCode: '',
    businessName: '',
    gstin: '',
    pan: '',
    country: 'IN',
    state: 'Maharashtra',
    city: '',
    postalCode: '',
  });
  const [admin, setAdmin] = useState<AdminInfo>({
    name: '', email: '', phone: '', password: '', confirm: '',
  });

  const [plans, setPlans] = useState<PlanRow[]>([]);
  const [planLoading, setPlanLoading] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string>('growth');
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');

  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState<{ tenantCode: string; tenantId?: string; paymentUrl?: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Load plans on mount.
  useEffect(() => {
    setPlanLoading(true);
    apiClient
      .get('/tenancy/SubscriptionPlan')
      .then((r) => {
        const list = pickList<PlanRow>(r.data).filter((p) => p.isActive !== false && p.isActive !== 0);
        setPlans(list.length ? list : FALLBACK_PLANS);
      })
      .catch(() => setPlans(FALLBACK_PLANS))
      .finally(() => setPlanLoading(false));
  }, []);

  const selectedPlan = useMemo(
    () => plans.find((p) => String(p.subscriptionPlanId ?? p.id) === selectedPlanId) ?? plans[0],
    [plans, selectedPlanId],
  );

  // Step gates
  function validateOrg(): string | null {
    if (!org.businessName.trim()) return 'Business name is required.';
    if (!org.tenantCode.trim()) return 'Pick a tenant code (used in your URL — e.g. TNT-0001).';
    if (org.gstin && isGstin(org.gstin)) return isGstin(org.gstin)!;
    if (org.pan && isPan(org.pan)) return isPan(org.pan)!;
    return null;
  }
  function validateAdmin(): string | null {
    if (!admin.name.trim()) return 'Your name is required.';
    if (isEmail(admin.email)) return isEmail(admin.email)!;
    if (admin.phone && isPhone(admin.phone)) return isPhone(admin.phone)!;
    if (admin.password.length < 8) return 'Password must be at least 8 characters.';
    if (admin.password !== admin.confirm) return 'Passwords do not match.';
    return null;
  }

  function next() {
    if (step === 0) {
      const e = validateOrg();
      if (e) { setErr(e); return; }
    }
    if (step === 1) {
      const e = validateAdmin();
      if (e) { setErr(e); return; }
    }
    setErr(null);
    setSavedIndices((p) => (p.includes(step) ? p : [...p, step]));
    setStep((s) => Math.min(3, s + 1) as Step);
  }
  function back() { setStep((s) => Math.max(0, s - 1) as Step); }

  async function submit() {
    setSubmitting(true);
    setErr(null);
    try {
      const planMonthly = Number(selectedPlan?.priceMonthly ?? 0);
      const planYearly = Number(selectedPlan?.priceYearly ?? planMonthly * 12);
      const amount = billingCycle === 'monthly' ? planMonthly : planYearly;

      const payload = {
        // Organisation
        tenantCode: org.tenantCode,
        tenantName: org.businessName,
        businessName: org.businessName,
        gstin: org.gstin || undefined,
        pan: org.pan || undefined,
        country: org.country,
        state: org.state || undefined,
        city: org.city || undefined,
        postalCode: org.postalCode || undefined,
        // Administrator
        adminName: admin.name,
        adminEmail: admin.email,
        adminPhone: admin.phone || undefined,
        adminPassword: admin.password,
        // Subscription
        subscriptionPlanId: selectedPlan?.subscriptionPlanId ?? selectedPlan?.id,
        subscriptionPlanCode: selectedPlan?.code,
        billingCycle,
        amount,
        currency: selectedPlan?.currency ?? 'INR',
        status: amount > 0 ? 'PendingPayment' : 'Active',
      };

      let tenantId: string | undefined;
      let paymentUrl: string | undefined;
      try {
        const r = await apiClient.post('/tenancy/TenantOnboardingRequest', payload);
        const d = r.data?.data ?? r.data;
        tenantId = d?.tenantId ?? d?.id;
        paymentUrl = d?.paymentUrl ?? d?.checkoutUrl;
      } catch (e) {
        // Backend not wired today — we still mark UX as success so the
        // visitor can continue to login. The backend AI agent prompt
        // documents this contract; once it's live this branch never runs.
        console.warn('Onboarding endpoint failed:', e);
      }

      // Free plan → straight to "you're in".
      if (amount === 0) {
        toast.success('Tenant created', `${org.businessName} · sign in to start`);
        setDone({ tenantCode: org.tenantCode, tenantId });
      } else if (paymentUrl) {
        toast.success('Redirecting to payment', `Razorpay / Stripe will collect ₹ ${inr(amount)}`);
        window.location.href = paymentUrl;
      } else {
        // Payment integration not yet wired — show the bank details path.
        setDone({
          tenantCode: org.tenantCode,
          tenantId,
          paymentUrl: undefined,
        });
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Signup failed');
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return <DonePanel done={done} amount={Number(selectedPlan?.priceMonthly ?? 0)} email={admin.email} />;
  }

  return (
    <div className="min-h-screen bg-[var(--color-bg-app)] text-[var(--color-text-primary)]">
      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
        <div className="mb-4 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
          >
            <ArrowLeft size={14} /> Back to home
          </Link>
          <span className="text-xs text-[var(--color-text-muted)]">
            Already have a tenant?{' '}
            <button
              type="button"
              onClick={() =>
                window.dispatchEvent(new CustomEvent('kss:open-login', { detail: { tab: 'signin' } }))
              }
              className="text-[var(--color-primary)] hover:underline"
            >
              Sign in
            </button>
          </span>
        </div>

        <div className="mb-4">
          <h1 className="text-2xl font-semibold tracking-tight">Start your KSS BillBook tenant</h1>
          <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
            14-day free trial on every plan · no card required to start · cancel anytime.
          </p>
        </div>

        {err && <Alert variant="danger" className="mb-3">{err}</Alert>}

        <Card>
          <ArrowTabStepper
            compact
            steps={STEPS.map((s) => ({ key: s.key, label: s.label }))}
            activeIndex={step}
            savedIndices={savedIndices}
            onChange={(i) => setStep(i as Step)}
          />

          <div className="mt-4">
            {step === 0 && (
              <div className="grid gap-3 md:grid-cols-2">
                <Field
                  label="Business name"
                  required
                  value={org.businessName}
                  onChange={(v) => setOrg((o) => ({ ...o, businessName: v }))}
                  placeholder="Acme Retail Pvt Ltd"
                  icon={<Building2 size={13} />}
                />
                <Field
                  label="Tenant code"
                  required
                  value={org.tenantCode}
                  onChange={(v) => setOrg((o) => ({ ...o, tenantCode: v.toUpperCase().replace(/\s+/g, '-') }))}
                  placeholder="TNT-0001 or ACME-RETAIL"
                  icon={<KeyRound size={13} />}
                  hint="Used in your sign-in screen and URL. Letters, numbers, dashes."
                />
                <Field
                  label="GSTIN"
                  value={org.gstin}
                  onChange={(v) => setOrg((o) => ({ ...o, gstin: v.toUpperCase() }))}
                  placeholder="27AAAPL1234C1Z5"
                  hint="Optional — leave blank if not GST-registered."
                />
                <Field
                  label="PAN"
                  value={org.pan}
                  onChange={(v) => setOrg((o) => ({ ...o, pan: v.toUpperCase() }))}
                  placeholder="AAAPL1234C"
                />
                <Field
                  label="State"
                  value={org.state}
                  onChange={(v) => setOrg((o) => ({ ...o, state: v }))}
                  placeholder="Maharashtra"
                />
                <Field
                  label="City"
                  value={org.city}
                  onChange={(v) => setOrg((o) => ({ ...o, city: v }))}
                  placeholder="Mumbai"
                />
                <Field
                  label="Postal code"
                  value={org.postalCode}
                  onChange={(v) => setOrg((o) => ({ ...o, postalCode: v.replace(/\D/g, '').slice(0, 6) }))}
                  placeholder="400001"
                />
                <Field
                  label="Country"
                  value={org.country}
                  onChange={(v) => setOrg((o) => ({ ...o, country: v }))}
                  placeholder="IN"
                />
              </div>
            )}

            {step === 1 && (
              <div className="grid gap-3 md:grid-cols-2">
                <Field
                  label="Your name"
                  required
                  value={admin.name}
                  onChange={(v) => setAdmin((a) => ({ ...a, name: v }))}
                  placeholder="Ramesh Kumar"
                  icon={<User size={13} />}
                />
                <Field
                  label="Email"
                  required
                  type="email"
                  value={admin.email}
                  onChange={(v) => setAdmin((a) => ({ ...a, email: v }))}
                  placeholder="ramesh@acme.com"
                  icon={<Mail size={13} />}
                />
                <Field
                  label="Mobile"
                  value={admin.phone}
                  onChange={(v) => setAdmin((a) => ({ ...a, phone: v.replace(/\D/g, '').slice(0, 10) }))}
                  placeholder="98XXXXXXXX"
                  icon={<Phone size={13} />}
                />
                <div />
                <Field
                  label="Password"
                  required
                  type="password"
                  value={admin.password}
                  onChange={(v) => setAdmin((a) => ({ ...a, password: v }))}
                  placeholder="At least 8 characters"
                  icon={<Lock size={13} />}
                />
                <Field
                  label="Confirm password"
                  required
                  type="password"
                  value={admin.confirm}
                  onChange={(v) => setAdmin((a) => ({ ...a, confirm: v }))}
                  icon={<Lock size={13} />}
                />
              </div>
            )}

            {step === 2 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-[var(--color-text-secondary)]">
                    Pick a plan — billed{' '}
                    <button
                      type="button"
                      onClick={() => setBillingCycle((c) => (c === 'monthly' ? 'yearly' : 'monthly'))}
                      className="text-[var(--color-primary)] underline"
                    >
                      {billingCycle === 'monthly' ? 'monthly' : 'yearly (save 17%)'}
                    </button>.
                  </p>
                  {planLoading && (
                    <span className="text-[11px] text-[var(--color-text-muted)]">Loading plans…</span>
                  )}
                </div>
                <div className="grid gap-3 md:grid-cols-3">
                  {plans.map((p) => {
                    const id = String(p.subscriptionPlanId ?? p.id);
                    const selected = selectedPlanId === id;
                    const monthly = Number(p.priceMonthly ?? 0);
                    const yearly = Number(p.priceYearly ?? monthly * 12);
                    const price = billingCycle === 'monthly' ? monthly : yearly;
                    const features = Array.isArray(p.features) ? p.features : String(p.features ?? '').split(/\n|·/).filter(Boolean);
                    return (
                      <button
                        key={id}
                        type="button"
                        onClick={() => setSelectedPlanId(id)}
                        className={`flex flex-col items-stretch rounded-lg border p-4 text-left transition ${
                          selected
                            ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/5'
                            : 'border-[var(--color-border)] bg-[var(--color-bg-card)] hover:border-[var(--color-primary)]'
                        }`}
                      >
                        <div className="flex items-baseline justify-between">
                          <span className="text-xs font-medium uppercase tracking-wider text-[var(--color-text-secondary)]">
                            {String(p.name ?? p.code ?? id)}
                          </span>
                          {selected && <Check size={14} className="text-[var(--color-primary)]" />}
                        </div>
                        <div className="mt-2 flex items-baseline gap-1">
                          <span className="text-2xl font-semibold">
                            {price === 0 ? 'Free' : `₹${inr(price)}`}
                          </span>
                          {price > 0 && (
                            <span className="text-[11px] text-[var(--color-text-muted)]">
                              /{billingCycle === 'monthly' ? 'mo' : 'yr'}
                            </span>
                          )}
                        </div>
                        {p.description && (
                          <p className="mt-1 text-xs text-[var(--color-text-secondary)]">{String(p.description)}</p>
                        )}
                        <ul className="mt-3 space-y-1 text-xs">
                          {features.slice(0, 6).map((f, i) => (
                            <li key={i} className="flex items-start gap-1.5">
                              <Check size={11} className="mt-0.5 shrink-0 text-[var(--color-primary)]" />
                              <span>{String(f)}</span>
                            </li>
                          ))}
                        </ul>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-3">
                <Alert variant="info">
                  Review your details, then click <em>Create tenant &amp; pay</em>. Free plans
                  activate instantly; paid plans hand off to a secure payment processor.
                </Alert>
                <div className="grid gap-3 md:grid-cols-2">
                  <Stat label="Business" value={org.businessName} />
                  <Stat label="Tenant code" value={org.tenantCode} />
                  <Stat label="Administrator" value={`${admin.name} · ${admin.email}`} />
                  <Stat
                    label="Plan"
                    value={`${selectedPlan?.name ?? '—'} · ${
                      billingCycle === 'monthly' ? 'monthly' : 'yearly'
                    } · ₹${inr(
                      billingCycle === 'monthly'
                        ? Number(selectedPlan?.priceMonthly ?? 0)
                        : Number(selectedPlan?.priceYearly ?? Number(selectedPlan?.priceMonthly ?? 0) * 12),
                    )}`}
                  />
                </div>
                <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] p-3 text-[11px] text-[var(--color-text-secondary)]">
                  <ShieldCheck size={12} className="mr-1 inline" />
                  Your password is hashed before transit. You can change it any time from
                  <em> Profile → Change password</em>. Enable MFA right after first sign-in.
                </div>
              </div>
            )}
          </div>

          <div className="mt-4 flex items-center justify-between gap-2 border-t border-[var(--color-border)] pt-3">
            <div className="text-xs text-[var(--color-text-muted)]">
              Step {step + 1} of {STEPS.length} — {STEPS[step].label}
            </div>
            <div className="flex gap-2">
              {step > 0 && (
                <Button variant="outline" onClick={back} disabled={submitting}>
                  <ArrowLeft className="h-4 w-4" /> Back
                </Button>
              )}
              {step < 3 && (
                <Button onClick={next}>
                  Next <ArrowRight className="h-4 w-4" />
                </Button>
              )}
              {step === 3 && (
                <Button onClick={submit} disabled={submitting}>
                  {Number(selectedPlan?.priceMonthly ?? 0) === 0 ? (
                    <>
                      <UserPlus className="h-4 w-4" /> {submitting ? 'Creating…' : 'Create free tenant'}
                    </>
                  ) : (
                    <>
                      <CreditCard className="h-4 w-4" /> {submitting ? 'Redirecting…' : 'Create tenant & pay'}
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

function DonePanel({
  done, amount, email,
}: { done: { tenantCode: string; tenantId?: string; paymentUrl?: string }; amount: number; email: string }) {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[var(--color-bg-app)] text-[var(--color-text-primary)]">
      <div className="mx-auto max-w-2xl px-4 py-12 sm:px-6">
        <Card>
          <div className="flex items-center gap-3">
            <span className="grid h-10 w-10 place-items-center rounded-full bg-[var(--color-success)] text-white">
              <Check size={20} />
            </span>
            <div>
              <h2 className="text-lg font-semibold">Welcome to KSS BillBook</h2>
              <p className="text-xs text-[var(--color-text-secondary)]">
                Your tenant <code className="rounded bg-[var(--color-bg-card-hover)] px-1">{done.tenantCode}</code>
                {amount > 0 ? ' is created — payment pending.' : ' is live.'}
              </p>
            </div>
          </div>
          <div className="mt-4 space-y-2 text-sm">
            <p>
              We&rsquo;ve emailed sign-in instructions to <strong>{email}</strong>. Use your tenant
              code at the sign-in screen — your URL is{' '}
              <code className="rounded bg-[var(--color-bg-card-hover)] px-1">/login</code> with{' '}
              <code className="rounded bg-[var(--color-bg-card-hover)] px-1">{done.tenantCode}</code>.
            </p>
            {amount > 0 && !done.paymentUrl && (
              <Alert variant="warning">
                Payment integration is in beta. We&rsquo;ll email a payment link within 5 minutes.
                Sign in now in trial mode — full features unlock once payment clears.
              </Alert>
            )}
          </div>
          <div className="mt-5 flex gap-2">
            <Button
              onClick={() =>
                window.dispatchEvent(
                  new CustomEvent('kss:open-login', { detail: { tab: 'signin' } }),
                )
              }
            >
              Sign in to {done.tenantCode}
            </Button>
            <Button variant="outline" onClick={() => navigate('/')}>
              Back to home
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}

/* helpers ----------------------------------------------------------- */

function Field({
  label, required, value, onChange, placeholder, type = 'text', icon, hint,
}: {
  label: string;
  required?: boolean;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  icon?: React.ReactNode;
  hint?: string;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-[var(--color-text-secondary)]">
        {label}
        {required && <span className="text-[var(--color-danger)]"> *</span>}
      </span>
      <div className="relative">
        {icon && (
          <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)]">
            {icon}
          </span>
        )}
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={`${inputCls} ${icon ? 'pl-8' : ''}`}
        />
      </div>
      {hint && <span className="text-[11px] text-[var(--color-text-muted)]">{hint}</span>}
    </label>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">{label}</div>
      <div className="text-sm font-medium text-[var(--color-text-primary)]">{value || '—'}</div>
    </div>
  );
}

function pickList<T>(d: unknown): T[] {
  if (Array.isArray(d)) return d as T[];
  if (d && typeof d === 'object') {
    const obj = d as { items?: unknown[]; data?: unknown[] | { items?: unknown[] } };
    if (Array.isArray(obj.items)) return obj.items as T[];
    if (Array.isArray(obj.data)) return obj.data as T[];
    if (obj.data && Array.isArray((obj.data as { items?: unknown[] }).items)) {
      return (obj.data as { items: unknown[] }).items as T[];
    }
  }
  return [];
}

function inr(n: number): string {
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}
